import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const fmt$ = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function DonorCardsView({ donors, navigate, stageColor, stageLabel }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="donor-cards-view">
      {donors.map(d => (
        <Card key={d.id} className="cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform]"
              onClick={() => navigate(`/donors/${d.id}`)} data-testid={`donor-card-${d.id}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#6B3FA0] to-[#9B7ED8] text-white flex items-center justify-center font-bold shrink-0">
                {(d.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{d.name}</div>
                <div className="text-xs text-gray-500 truncate">{d.email || 'no email'}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <Badge className={`${stageColor(d.pipeline_stage)} border-0 text-[10px]`}>{stageLabel(d.pipeline_stage)}</Badge>
              <div className="text-right">
                <div className="font-bold">{fmt$(d.total_given)}</div>
                <div className="text-[11px] text-gray-500">{d.gift_count} gift{d.gift_count !== 1 ? 's' : ''}{d.last_gift_date ? ` · last ${d.last_gift_date}` : ''}</div>
              </div>
            </div>
            {(d.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {d.tags.slice(0, 3).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                {d.tags.length > 3 && <span className="text-[10px] text-gray-400">+{d.tags.length - 3}</span>}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const COLUMNS = [
  { key: 'last_name', label: 'Name', title: 'Sorts by last name' },
  { key: 'pipeline_stage', label: 'Stage' },
  { key: 'total_given', label: 'Lifetime', align: 'right' },
  { key: 'gift_count', label: 'Gifts', align: 'right' },
  { key: 'last_gift_date', label: 'Last gift' },
  { key: 'created_at', label: 'Added' },
];

export function DonorTableView({ donors, navigate, stageColor, stageLabel, sortBy, sortDir, onSort }) {
  return (
    <Card data-testid="donor-table-view">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-[#F7F4EF] text-xs uppercase tracking-wide text-gray-500">
              {COLUMNS.map(c => (
                <th key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                  <button className="inline-flex items-center gap-1 hover:text-[#6B3FA0]" title={c.title || ''}
                          onClick={() => onSort(c.key)} data-testid={`donor-th-${c.key}`}>
                    {c.label}
                    {sortBy === c.key
                      ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                      : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
              ))}
              <th className="px-3 py-2 text-left text-xs">Email</th>
            </tr>
          </thead>
          <tbody>
            {donors.map(d => (
              <tr key={d.id} className="border-b last:border-b-0 hover:bg-purple-50/40 cursor-pointer"
                  onClick={() => navigate(`/donors/${d.id}`)} data-testid={`donor-tr-${d.id}`}>
                <td className="px-3 py-2 font-medium">{d.name}</td>
                <td className="px-3 py-2"><Badge className={`${stageColor(d.pipeline_stage)} border-0 text-[10px]`}>{stageLabel(d.pipeline_stage)}</Badge></td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmt$(d.total_given)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{d.gift_count}</td>
                <td className="px-3 py-2 text-gray-600">{d.last_gift_date || '—'}</td>
                <td className="px-3 py-2 text-gray-500">{(d.created_at || '').slice(0, 10)}</td>
                <td className="px-3 py-2 text-gray-500 truncate max-w-[220px]">{d.email || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
